import styles from "./my-requests.module.css";
import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Flex, Typography, App, Spin, Modal, Form, Input } from "antd";

import Breadcrumbs from "@/components/ui/Breadcrumbs";
import MyRequestsTable from "@/components/tables/MyRequestsTable";
import CustomSelect from "@/components/inputs/CustomSelect";
import FilterCalendar from "@/components/inputs/FilterCalendar";

import { UserContext } from "@/context/UserContext";

import { useTableAutoHeight } from "@/hooks/useTableAutoHeight";

import {
  getMyRequests,
  getFormList as getTypes,
  cancelMyRequest,
} from "@/api/requests";
import { STATUSES } from "@/constants/requests";

import routes from "@/routes";

const { Title } = Typography;

const LOCAL_STORAGE_KEY = "myRequestsTableParams";

const MyRequests = () => {
  const { message } = App.useApp();
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  // Данные
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [optionsPending, setOptionsPending] = useState(true);
  const [typeOptions, setTypeOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [cancelModalRequest, setCancelModalRequest] = useState(null);
  const [cancelPending, setCancelPending] = useState(false);
  const [cancelForm] = Form.useForm();

  const initialTableParams = {
    request_form_uuid: null,
    status: null,
    created_at: {},
    sort: "-created_at",
  };

  const [tableParams, setTableParams] = useState(initialTableParams);

  // Автоматическая высота таблицы
  const { tableContainerRef, tableHeight, headerHeight } = useTableAutoHeight([
    data,
  ]);

  //  initial fetch
  useEffect(() => {
    fetchOptions();

    // eslint-disable-next-line
  }, []);

  const fetchOptions = async () => {
    setOptionsPending(true);
    try {
      const types = await getTypes();
      setTypeOptions(types);
      setStatusOptions(STATUSES);

      // Инициализация параметров из localStorage
      const savedParams =
        JSON.parse(
          localStorage.getItem(`${user.user_uuid}_${LOCAL_STORAGE_KEY}`),
        ) || initialTableParams;
      const localParams = normalizeTableParams(savedParams);

      setTableParams(localParams);
      fetchData(localParams);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setOptionsPending(false);
    }
  };

  // Запрос на сервер для получения данных
  const fetchData = async (tParams) => {
    setIsLoading(true);

    const params = { ...tParams } || { ...tableParams };

    try {
      const res = await getMyRequests(formatFilters(params));
      setData(res);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatFilters = (params) => {
    return Object.keys(params).reduce((acc, val) => {
      if (val === "created_at") {
        acc[val] = JSON.stringify(params[val]);
      } else {
        acc[val] = params[val];
      }
      return acc;
    }, {});
  };

  const normalizeTableParams = (params) => {
    if (!Object.prototype.hasOwnProperty.call(params, "is_completed")) {
      return params;
    }

    const { is_completed, ...rest } = params;

    return {
      ...rest,
      sort:
        rest.sort === "is_completed"
          ? "status"
          : rest.sort === "-is_completed"
            ? "-status"
            : rest.sort,
      status:
        is_completed === true
          ? "completed"
          : is_completed === false
            ? "registered"
            : null,
    };
  };

  // Обработчик для смены фильтров
  const handleFilterChange = async (name, val) => {
    updateFilters(tableParams, {
      [name]: val,
    });
  };

  const updateFilters = (localParams, newFilters) => {
    const updatedTableParams = { ...localParams, ...newFilters };

    setParams(updatedTableParams);
    fetchData(updatedTableParams);
  };

  // Устанавливаем параметры таблицы и сохраняем их в localStorage
  const setParams = (params) => {
    setTableParams(params);

    // Сохраняем параметры пагинации в localStorage
    localStorage.setItem(
      `${user.user_uuid}_${LOCAL_STORAGE_KEY}`,
      JSON.stringify(params),
    );
  };

  const handleTableChange = (pagination, filters, sorter) => {
    const updatedTableParams = {
      ...tableParams,

      ...(sorter?.order && {
        sort: `${sorter.order === "descend" ? "-" : ""}${sorter.field}`,
      }),
    };

    setParams(updatedTableParams);
    fetchData(updatedTableParams);
  };

  const handleCancelRequestClick = (request) => {
    const canCancelRequest = request.can_cancel === true;

    if (!canCancelRequest) {
      Modal.warning({
        title: "Отмена невозможна",
        content:
          "По заявке уже выполняются работы. Отмена заявки невозможна.",
        okText: "Понятно",
      });
      return;
    }

    setCancelModalRequest(request);
    cancelForm.resetFields();
  };

  const handleCancelRequest = async () => {
    try {
      const values = await cancelForm.validateFields();

      setCancelPending(true);
      await cancelMyRequest({
        uuid: cancelModalRequest.uuid,
        reason: values.reason || "",
      });

      message.open({
        type: "success",
        content: "Заявка отменена",
      });

      setCancelModalRequest(null);
      cancelForm.resetFields();
      fetchData(tableParams);
    } catch (error) {
      if (error?.errorFields) return;

      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setCancelPending(false);
    }
  };

  const handleOpenRequest = (request) => {
    navigate(routes.myRequestDetail(request.uuid));
  };

  return (
    <>
      {optionsPending ? (
        <div className="spin">
          <Spin size="large" />
        </div>
      ) : (
        data && (
          <Flex vertical gap={25} className="h-100">
            <Flex vertical gap={10}>
              <Breadcrumbs
                items={[
                  {
                    title: "Главная",
                    href: routes.dashboards,
                  },
                  {
                    title: "Заявки",
                  },
                  {
                    title: "Мои заявки",
                  },
                ]}
              />

              <Title level={2} className="no-margin">
                Мои заявки
              </Title>
            </Flex>

            <Flex
              gap={8}
              align="center"
              justify="space-between"
              wrap
              className={styles.filtersWrap}
            >
              <Flex gap={8} align="center" wrap className={styles.filters}>
                <CustomSelect
                  placeholder="Тип"
                  options={typeOptions}
                  fieldNames={{
                    label: "name",
                    value: "uuid",
                    key: "uuid",
                  }}
                  style={{
                    width: 200,
                  }}
                  showSearch
                  optionFilterProp="name"
                  allowClear
                  placement="bottomLeft"
                  popupMatchSelectWidth={false}
                  value={tableParams.request_form_uuid}
                  onChange={(type) =>
                    handleFilterChange("request_form_uuid", type)
                  }
                />

                <CustomSelect
                  placeholder="Статус"
                  options={statusOptions}
                  style={{
                    width: 150,
                  }}
                  showSearch
                  allowClear
                  placement="bottomLeft"
                  popupMatchSelectWidth={false}
                  value={tableParams.status}
                  onChange={(status) =>
                    handleFilterChange("status", status)
                  }
                />
              </Flex>

              <div className={styles.filters_calendar}>
                <FilterCalendar
                  value={tableParams.created_at}
                  type="date"
                  onSubmit={(val) => handleFilterChange("created_at", val)}
                />
              </div>
            </Flex>

            <div ref={tableContainerRef} style={{ flex: 1, minHeight: 0 }}>
              <MyRequestsTable
                data={data}
                loading={isLoading}
                onChange={handleTableChange}
                onCancelRequest={handleCancelRequestClick}
                onOpenRequest={handleOpenRequest}
                scroll={{ x: "100%", y: tableHeight - headerHeight }}
              />
            </div>

            <Modal
              maskClosable={false}
              title="Отмена заявки"
              open={!!cancelModalRequest}
              onOk={handleCancelRequest}
              onCancel={() => setCancelModalRequest(null)}
              okText="Отменить заявку"
              cancelText="Закрыть"
              okButtonProps={{ loading: cancelPending }}
            >
              <Form form={cancelForm} layout="vertical">
                <Form.Item
                  name="reason"
                  label="Укажите причину отмены"
                >
                  <Input.TextArea rows={4} allowClear />
                </Form.Item>
              </Form>
            </Modal>
          </Flex>
        )
      )}
    </>
  );
};

export default MyRequests;
